import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
// ProjectContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useProjects } from "../../../contexts/ProjectContext";
import {
  agentToolsApi,
  type ListMCPConnectionsParams,
  type PaginationMetadata,
} from "../../../services/agent-tools-service";
import { mapConnectionDtoToUi } from "../adapters";
import type { MCPConnection } from "../types";

export const MCP_CONNECTIONS_QUERY_KEY = ["mcp-connections"] as const;

type UseMCPConnectionsResult = {
  connections: MCPConnection[];
  metadata: PaginationMetadata | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
};

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 20;

export const useMCPConnections = (
  params: ListMCPConnectionsParams = {},
): UseMCPConnectionsResult => {
  const { i18n } = useTranslation();
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const locale = i18n.resolvedLanguage ?? i18n.language;

  // The list is filtered by the selected projects, so they have to be part of
  // the cache key — otherwise switching projects keeps serving the previous
  // selection's connections until the entry goes stale. An "all" selection
  // sends no project_ids at all and lets the API decide the scope.
  const { selectedProjects } = useProjects();
  const projectIds = useMemo<string[]>(() => {
    const selection: string[] = Array.isArray(selectedProjects)
      ? selectedProjects
      : [];
    return selection.includes("all") ? [] : selection;
  }, [selectedProjects]);

  // One stable string so the key compares by value, not array identity.
  const projectScope = projectIds.join(",") || "all";

  // The scope is appended rather than inserted: the optimistic-update and
  // delete paths spot list pages by `key[1]` being the page number.
  const query = useQuery({
    queryKey: [...MCP_CONNECTIONS_QUERY_KEY, page, pageSize, projectScope],
    queryFn: () =>
      agentToolsApi.listMCPConnections({ page, pageSize, projectIds }),
    // Keep the previous page's rows while paginating, but not across a project
    // switch — showing another selection's connections under a new scope would
    // read as if they belonged to it.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey?.[3] === projectScope ? previous : undefined,
  });

  const connections = useMemo(
    () =>
      (query.data?.items ?? []).map((connection) =>
        mapConnectionDtoToUi(connection, locale),
      ),
    [locale, query.data],
  );

  return {
    connections,
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
  };
};
